// src/pages/AdvanceReportPage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Chip,
  Stack,
} from "@mui/material";

// Icons for each report
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CompareIcon from "@mui/icons-material/Compare";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import CategoryIcon from "@mui/icons-material/Category";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

// Define your filter categories
const filters = [
  { key: "all", label: "All" },
  { key: "variance", label: "Variance" },
  { key: "cashflow", label: "Cashflow" },
  { key: "operations", label: "Operations" },
  { key: "reconciliation", label: "Reconciliation" },
];

// Attach a category to each report
const reports = [
  {
    label: "Till Variance",
    category: "variance",
    Icon: AccountBalanceWalletIcon,
    path: "/advance-reports/till-variance",
  },
  {
    label: "C2B Variance",
    category: "variance",
    Icon: CompareIcon,
    path: "/advance-reports/c2b-variance",
  },
  {
    label: "Cash In",
    category: "cashflow",
    Icon: ArrowCircleUpIcon,
    path: "/advance-reports/cashin",
  },
  {
    label: "Cash Out",
    category: "cashflow",
    Icon: ArrowCircleDownIcon,
    path: "/advance-reports/cashout",
  },
  {
    label: "Cash Sales",
    category: "cashflow",
    Icon: PointOfSaleIcon,
    path: "/advance-reports/cashsales",
  },
  {
    label: "Driver Wage",
    category: "operations",
    Icon: DriveEtaIcon,
    path: "/advance-reports/driver-wage",
  },
  {
    label: "Category Report",
    category: "operations",
    Icon: CategoryIcon,
    path: "/advance-reports/category-report",
  },
  {
    label: "Issue Report",
    category: "operations",
    Icon: ReportProblemIcon,
    path: "/advance-reports/issue",
  },
  {
    label: "Reconciliation Report",
    category: "reconciliation",
    Icon: AssessmentIcon,
    path: "/advance-reports/reconciliation-report",
  },
  {
    label: "Expected C2B",
    category: "cashflow",
    Icon: ReceiptLongIcon,
    path: "/advance-reports/expected-c2b",
  },
  {
    label: "Target Cash",
    category: "cashflow",
    Icon: TrackChangesIcon,
    path: "/advance-reports/target-cash",
  },
  {
    label: "Declared Cash",
    category: "cashflow",
    Icon: MonetizationOnIcon,
    path: "/advance-reports/declared-cash",
  },
];

export default function AdvanceReportPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");

  // Filter logic
  const visibleReports =
    activeFilter === "all"
      ? reports
      : reports.filter((r) => r.category === activeFilter);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Typography variant="h4" align="center" gutterBottom>
        Advanced Reports
      </Typography>

      {/* Filter Bar */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        sx={{ mb: 3, flexWrap: "wrap" }}
      >
        {filters.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            clickable
            size="medium"
            variant={activeFilter === key ? "filled" : "outlined"}
            color={activeFilter === key ? "primary" : "default"}
            onClick={() => setActiveFilter(key)}
            sx={{ textTransform: "none" }}
          />
        ))}
      </Stack>

      {/* Report Grid */}
      <Grid container spacing={3}>
        {visibleReports.map(({ label, Icon, path }) => (
          <Grid item key={label} xs={12} sm={6} md={4} lg={3}>
            <Card
              elevation={1}
              sx={{
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 6 },
              }}
            >
              <CardActionArea
                onClick={() => navigate(path)}
                aria-label={`Go to ${label} report`}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                  }}
                >
                  <Icon fontSize="large" color="primary" />
                  <Typography
                    variant="button"
                    component="div"
                    sx={{ mt: 1, textAlign: "center" }}
                  >
                    {label}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
